/**
 * @file Fabrique procedurale d'armes a partir de (WeaponVisual, grade) : silhouette, canons, lunette, couleurs du grade, lueur ; point de bouche (muzzle).
 */
import { Color, Group, Object3D } from 'three';
import type { BufferGeometry } from 'three';
import type { GradeDef, GunSilhouette, WeaponDef } from '@config/types';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { ToonKit } from '../toon/toon';
import { MergeBuilder, mixHex } from './MergeBuilder';
import type { PartOptions } from './MergeBuilder';

/** Une piece de l'arme (geometrie dans le repere du groupe) : rendue par PartBatch avec la matrice monde du groupe. */
export interface GunPart {
  key: string;
  fill: BufferGeometry;
  /** Contour : porte par une seule piece (la premiere) pour toute l'arme. */
  hull: BufferGeometry | null;
  /** Piece a teinte animee (grades Z..) : sa couleur d'instance = GunFactory.shimmerColors[phase]. */
  shimmer: boolean;
  phase: number;
}

export interface GunModel {
  group: Group;
  /** Bouche du canon (enfant de group) : origine des projectiles a l'affichage et du muzzle flash. */
  muzzle: Object3D;
  /** Longueur totale (pour placer l'arme entre les pattes : poignee a l'origine du group). */
  length: number;
  /** Pieces a rendre (partagees entre toutes les instances du modele). */
  parts: readonly GunPart[];
}

/** Echelle globale de l'arme tenue = visual.size * GUN_UNIT (taille 1 = mini, 2.4 = geant). */
const GUN_UNIT = 0.22;
const WOOD = '#a9794a';
const DARK_METAL = '#2c2933';
const LENS = '#7fd3ff';
/** Periode du cycle de teinte des grades Z.. (secondes). */
const SHIMMER_PERIOD = 3;
/** Hauteur de l'axe du canon dans le repere du modele. */
const BORE_Y = 0.36;

interface Layout {
  /** z de la bouche et hauteur de l'axe ; rayon du canon (bague lumineuse). */
  muzzleZ: number;
  muzzleY: number;
  boreR: number;
  /** Longueur du corps (rayures shimmer, lunette). */
  bodyLen: number;
}

/**
 * Regles de construction (repere : +Z = direction du tir, poignee a l'origine, echelle globale = visual.size * 0.22) :
 *  - Corps par silhouette (17 silhouettes de WeaponVisual) ; scope 1/2 : 1 ou 2 lunettes ; bipod : 2 pieds fins ; drumMag : cylindre sous l'arme.
 *  - Couleurs : corps = grade.colors.primary, pieces/accents = grade.colors.secondary, crosse bois pour C ; grade.glow > 0 : anneau
 *    + lentille a materiau emissif (couleur glow) ; grade.shimmer (Z..ZZZ) : bande a materiau emissif dedie par grade dont la teinte cycle.
 *  - Une piece a couleurs de sommets par groupe de materiau (+ une coque de contour) rendues par PartBatch (2 draw calls pour toutes
 *    les armes). Lueur du grade = couleurs sur-lumineuses ; shimmer = piece blanche dont la couleur d'instance cycle en teinte.
 *    Un modele est mis en cache PAR defId ; les instances sont des group.clone(true) (le groupe ne porte que le muzzle).
 */
export class GunFactory {
  private readonly cache = new Map<string, GunModel>();
  /** Couleurs d'instance des pieces shimmer (3 phases decalees), mises a jour par updateShimmer. */
  readonly shimmerColors: readonly Color[] = [new Color(), new Color(), new Color()];

  constructor(
    readonly kit: ToonKit,
    private readonly geo: GeometryLibrary,
  ) {}

  /** Modele cache par def.id puis clone (group.clone(true) ; muzzle retrouve par son name 'muzzle'). */
  create(def: WeaponDef, grade: GradeDef): GunModel {
    let proto = this.cache.get(def.id);
    if (!proto) {
      proto = this.build(def, grade);
      this.cache.set(def.id, proto);
    }
    const group = proto.group.clone(true);
    const muzzle = group.getObjectByName('muzzle');
    if (!muzzle) throw new Error(`GunFactory: muzzle introuvable (${def.id})`);
    return { group, muzzle, length: proto.length, parts: proto.parts };
  }

  /** Fait cycler la teinte HSL des couleurs shimmer (periode 3 s). Appele 1x par frame par le renderer. */
  updateShimmer(time: number): void {
    this.shimmerColors.forEach((c, i) => c.setHSL((time / SHIMMER_PERIOD + i / this.shimmerColors.length) % 1, 0.75, 0.68));
  }

  // ------------------------------------------------------------------------------------------------------------------

  private build(def: WeaponDef, grade: GradeDef): GunModel {
    const v = def.visual;
    const g = this.geo;
    const b = new MergeBuilder();
    const P = grade.colors.primary;
    const S = grade.colors.secondary;
    const dark = mixHex(P, DARK_METAL, 0.6);
    const stock = grade.id === 'C' ? WOOD : dark;
    const glowGroup = grade.glow > 0 ? 'glow' : 'main';

    const box = (w: number, h: number, d: number, color: string, o: PartOptions): void => {
      b.add(g.box(w, h, d), color, o);
    };
    /** Cylindre d'axe Z de z0 a z0 + len. */
    const tube = (r: number, len: number, z0: number, color: string, o: PartOptions = {}, rb: number = r): void => {
      b.add(g.cylinder(r, rb, len, 12), color, { y: BORE_Y, ...o, z: z0 + len / 2, rx: Math.PI / 2 });
    };
    const grip = (z = 0.12): void => {
      box(0.26, 0.62, 0.28, dark, { y: -0.02, z, rx: -0.18 });
      box(0.3, 0.05, 0.4, S, { y: 0.3, z: z + 0.1, outline: false });
    };
    const mag = (z: number, h = 0.5): void => box(0.2, h, 0.3, S, { y: 0.1 - h / 2, z });
    const buttStock = (len = 0.8, h = 0.4): void => box(0.26, h, len, stock, { y: BORE_Y - 0.04, z: -len / 2 - 0.05 });

    const layout = ((): Layout => {
      const sil: GunSilhouette = v.silhouette;
      switch (sil) {
        case 'pistol':
          box(0.28, 0.26, 1.25, P, { y: BORE_Y, z: 0.62 });
          tube(0.07, 0.3, 1.25, S);
          box(0.05, 0.06, 0.05, S, { y: BORE_Y + 0.16, z: 1.45, outline: false });
          grip();
          return { muzzleZ: 1.55, muzzleY: BORE_Y, boreR: 0.07, bodyLen: 1.25 };
        case 'revolver':
          box(0.3, 0.34, 0.75, P, { y: BORE_Y, z: 0.36 });
          tube(0.09, 1.0, 0.7, S);
          b.add(g.cylinder(0.23, 0.23, 0.46, 10), S, { y: BORE_Y - 0.02, z: 0.52, rx: Math.PI / 2 });
          box(0.07, 0.14, 0.1, dark, { y: BORE_Y + 0.22, z: -0.02 });
          grip();
          return { muzzleZ: 1.7, muzzleY: BORE_Y, boreR: 0.09, bodyLen: 1.1 };
        case 'smg':
          box(0.32, 0.36, 1.4, P, { y: BORE_Y, z: 0.7 });
          tube(0.075, 0.55, 1.4, S);
          mag(0.7);
          box(0.2, 0.3, 0.22, S, { y: -0.34, z: 0.6, rx: 0.3 });
          buttStock(0.5, 0.3);
          grip(0.06);
          return { muzzleZ: 1.95, muzzleY: BORE_Y, boreR: 0.075, bodyLen: 1.4 };
        case 'shotgun':
          box(0.3, 0.32, 1.0, P, { y: BORE_Y, z: 0.45 });
          tube(0.12, 1.7, 0.9, S);
          tube(0.1, 0.55, 1.2, dark, { y: BORE_Y - 0.17 });
          buttStock(0.8);
          grip();
          return { muzzleZ: 2.6, muzzleY: BORE_Y, boreR: 0.12, bodyLen: 1.0 };
        case 'doubleShotgun':
          box(0.34, 0.32, 0.9, P, { y: BORE_Y, z: 0.4 });
          tube(0.1, 1.6, 0.8, S, { x: 0.11 });
          tube(0.1, 1.6, 0.8, S, { x: -0.11 });
          buttStock(0.8);
          grip();
          return { muzzleZ: 2.4, muzzleY: BORE_Y, boreR: 0.22, bodyLen: 0.9 };
        case 'pumpShotgun':
          box(0.3, 0.32, 1.0, P, { y: BORE_Y, z: 0.45 });
          tube(0.12, 1.7, 0.9, S);
          tube(0.14, 0.7, 1.2, dark, { y: BORE_Y - 0.2 });
          buttStock(0.8);
          grip();
          return { muzzleZ: 2.6, muzzleY: BORE_Y, boreR: 0.12, bodyLen: 1.0 };
        case 'drumShotgun':
          box(0.32, 0.34, 1.0, P, { y: BORE_Y, z: 0.45 });
          tube(0.13, 1.7, 0.9, S);
          tube(0.09, 0.6, 1.2, dark, { y: BORE_Y - 0.17 });
          buttStock(0.8);
          grip();
          return { muzzleZ: 2.6, muzzleY: BORE_Y, boreR: 0.13, bodyLen: 1.0 };
        case 'rifle':
          box(0.28, 0.34, 1.35, P, { y: BORE_Y, z: 0.5 });
          box(0.3, 0.26, 0.9, S, { y: BORE_Y - 0.02, z: 1.55 });
          tube(0.07, 1.4, 1.8, S);
          mag(0.45);
          buttStock(0.9);
          grip();
          return { muzzleZ: 3.2, muzzleY: BORE_Y, boreR: 0.07, bodyLen: 1.35 };
        case 'boltRifle':
          box(0.26, 0.32, 1.2, P, { y: BORE_Y, z: 0.45 });
          tube(0.07, 1.8, 1.05, S);
          b.add(g.sphere(0.07, 8, 6), S, { x: 0.18, y: BORE_Y + 0.08, z: 0.2 });
          box(0.04, 0.04, 0.18, S, { x: 0.12, y: BORE_Y + 0.08, z: 0.2 });
          buttStock(1.0, 0.42);
          grip();
          return { muzzleZ: 2.85, muzzleY: BORE_Y, boreR: 0.07, bodyLen: 1.2 };
        case 'railgun':
          box(0.32, 0.36, 1.4, P, { y: BORE_Y, z: 0.5 });
          box(0.09, 0.12, 2.2, S, { x: 0.17, y: BORE_Y + 0.16, z: 1.5 });
          box(0.09, 0.12, 2.2, S, { x: -0.17, y: BORE_Y + 0.16, z: 1.5 });
          for (const z of [0.9, 1.5, 2.1]) b.add(g.torus(0.27, 0.045), grade.colors.glow, { y: BORE_Y + 0.16, z, group: glowGroup });
          grip();
          return { muzzleZ: 2.65, muzzleY: BORE_Y + 0.16, boreR: 0.16, bodyLen: 1.4 };
        case 'minigun':
        case 'gatling': {
          const n = Math.max(1, v.barrels);
          const ringR = n <= 1 ? 0 : 0.14 + n * 0.012;
          b.add(g.cylinder(0.34, 0.34, 0.9, 14), P, { y: BORE_Y + 0.1, z: 0.4, rx: Math.PI / 2 });
          box(0.42, 0.36, 0.55, dark, { y: -0.18, z: 0.3 });
          box(0.12, 0.2, 0.5, S, { y: BORE_Y + 0.5, z: 0.3 });
          for (let i = 0; i < n; i++) {
            const a = (i / n) * Math.PI * 2;
            tube(0.055, 1.5, 0.9, S, { x: Math.sin(a) * ringR, y: BORE_Y + 0.1 + Math.cos(a) * ringR });
          }
          for (const z of [1.35, 2.2]) b.add(g.cylinder(ringR + 0.1, ringR + 0.1, 0.1, 14), dark, { y: BORE_Y + 0.1, z, rx: Math.PI / 2 });
          grip(0.02);
          return { muzzleZ: 2.4, muzzleY: BORE_Y + 0.1, boreR: ringR + 0.1, bodyLen: 0.9 };
        }
        case 'cannon':
          box(0.5, 0.5, 0.5, dark, { y: BORE_Y + 0.1, z: 0.1 });
          tube(0.3, 1.6, 0.3, P, { y: BORE_Y + 0.1 });
          for (const z of [0.8, 1.4]) tube(0.33, 0.1, z, S, { y: BORE_Y + 0.1 });
          tube(0.42, 0.3, 1.9, S, { y: BORE_Y + 0.1 }, 0.3);
          grip(0.0);
          return { muzzleZ: 2.2, muzzleY: BORE_Y + 0.1, boreR: 0.42, bodyLen: 1.6 };
        case 'grenadeLauncher':
          tube(0.26, 1.2, 0.3, P, { y: BORE_Y + 0.05 });
          tube(0.31, 0.2, 1.5, S, { y: BORE_Y + 0.05 }, 0.26);
          box(0.4, 0.34, 0.5, dark, { y: BORE_Y - 0.02, z: 0.1 });
          box(0.06, 0.14, 0.1, S, { y: BORE_Y + 0.4, z: 1.0 });
          buttStock(0.5, 0.3);
          grip();
          return { muzzleZ: 1.7, muzzleY: BORE_Y + 0.05, boreR: 0.31, bodyLen: 1.2 };
        case 'sniper':
          box(0.26, 0.34, 1.3, P, { y: BORE_Y, z: 0.45 });
          tube(0.06, 2.2, 1.1, S);
          box(0.14, 0.14, 0.22, S, { y: BORE_Y, z: 3.2 });
          buttStock(0.9, 0.42);
          grip();
          return { muzzleZ: 3.3, muzzleY: BORE_Y, boreR: 0.07, bodyLen: 1.3 };
        case 'launcher': {
          const n = Math.max(1, v.barrels);
          const cols = Math.ceil(Math.sqrt(n));
          const rows = Math.ceil(n / cols);
          const pitch = 0.26;
          for (let i = 0; i < n; i++) {
            const cx = (i % cols) - (cols - 1) / 2;
            const cy = Math.floor(i / cols) - (rows - 1) / 2;
            box(0.22, 0.22, 1.5, i % 2 === 0 ? P : S, { x: cx * pitch, y: BORE_Y + 0.2 + cy * pitch, z: 1.05 });
          }
          box(cols * pitch + 0.1, rows * pitch + 0.1, 0.3, dark, { y: BORE_Y + 0.2, z: 0.35 });
          grip();
          return { muzzleZ: 1.8, muzzleY: BORE_Y + 0.2, boreR: 0.3, bodyLen: 1.2 };
        }
        case 'energyCannon':
          tube(0.22, 1.5, 0.3, P, { y: BORE_Y + 0.1 });
          box(0.4, 0.4, 0.5, dark, { y: BORE_Y + 0.1, z: 0.1 });
          for (const z of [0.65, 1.05, 1.45]) b.add(g.torus(0.3, 0.05), grade.colors.glow, { y: BORE_Y + 0.1, z, group: glowGroup });
          b.add(g.sphere(0.17, 10, 8), grade.colors.glow, { y: BORE_Y + 0.1, z: 1.85, group: glowGroup });
          box(0.05, 0.4, 0.5, S, { y: BORE_Y + 0.1, z: 0.9, rz: 0 });
          grip();
          return { muzzleZ: 2.05, muzzleY: BORE_Y + 0.1, boreR: 0.3, bodyLen: 1.5 };
      }
    })();

    // ---- options communes : lunettes, bipied, tambour
    if (v.scope > 0) {
      const xs = v.scope === 2 ? [-0.15, 0.15] : [0];
      for (const x of xs) {
        const y = layout.muzzleY + 0.3;
        const z0 = layout.bodyLen * 0.25;
        b.add(g.cylinder(0.09, 0.09, 0.75, 12), S, { x, y, z: z0 + 0.375, rx: Math.PI / 2 });
        b.add(g.cylinder(0.12, 0.12, 0.1, 12), dark, { x, y, z: z0 + 0.8, rx: Math.PI / 2 });
        b.add(g.cylinder(0.075, 0.075, 0.03, 12), LENS, { x, y, z: z0 + 0.86, rx: Math.PI / 2, group: glowGroup, outline: false });
        box(0.06, 0.12, 0.08, S, { x, y: y - 0.1, z: z0 + 0.15, outline: false });
      }
    }
    if (v.bipod) {
      const z = layout.muzzleZ * 0.62;
      for (const sx of [-1, 1]) b.add(g.cylinder(0.025, 0.025, 0.55, 6), dark, { x: sx * 0.13, y: -0.02, z: z + 0.1, rx: -0.45, rz: -sx * 0.25 });
    }
    if (v.drumMag && v.silhouette !== 'drumShotgun') {
      b.add(g.cylinder(0.3, 0.3, 0.28, 14), S, { y: -0.12, z: 0.62, rz: Math.PI / 2 });
    }
    if (v.silhouette === 'drumShotgun') {
      b.add(g.cylinder(0.33, 0.33, 0.3, 14), S, { y: -0.14, z: 0.62, rz: Math.PI / 2 });
    }

    // ---- lueur / reflet du grade : bague a la bouche (+ bande shimmer pour Z..)
    b.add(g.cylinder(layout.boreR + 0.04, layout.boreR + 0.04, 0.1, 14), grade.glow > 0 ? grade.colors.glow : S, {
      y: layout.muzzleY,
      z: layout.muzzleZ - 0.3,
      rx: Math.PI / 2,
      group: glowGroup,
    });
    if (grade.shimmer) {
      for (const sx of [-1, 1]) box(0.02, 0.12, layout.bodyLen * 0.8, '#ffffff', { x: sx * 0.16, y: BORE_Y, z: layout.bodyLen * 0.5, group: 'shimmer', outline: false });
      b.add(g.cylinder(layout.boreR + 0.07, layout.boreR + 0.07, 0.07, 14), '#ffffff', { y: layout.muzzleY, z: layout.muzzleZ - 0.55, rx: Math.PI / 2, group: 'shimmer' });
    }

    // ---- assemblage : une piece par groupe de materiau, contour commun porte par la premiere
    const group = new Group();
    group.name = `gun:${def.id}`;
    const parts: GunPart[] = [];
    const hullSrc = b.hull();
    for (const name of b.groups()) {
      const fill = b.fill(name);
      if (!fill) continue;
      if (name === 'glow') {
        // lueur du grade : couleurs sur-lumineuses (le materiau partage n'a pas d'emissive)
        const col = fill.getAttribute('color');
        const gain = 1.25 + 0.6 * grade.glow;
        for (let i = 0; i < col.array.length; i++) col.array[i] = (col.array[i] ?? 0) * gain;
      }
      const key = `gun:${def.id}:${name}`;
      const geometry = g.get(key, () => fill);
      const first = parts.length === 0;
      parts.push({
        key,
        fill: geometry,
        hull: first && hullSrc ? g.get(`${key}:hull`, () => hullSrc) : null,
        shimmer: name === 'shimmer',
        phase: grade.index % 3,
      });
    }

    const muzzle = new Object3D();
    muzzle.name = 'muzzle';
    muzzle.position.set(0, layout.muzzleY, layout.muzzleZ);
    group.add(muzzle);
    const scale = v.size * GUN_UNIT;
    group.scale.setScalar(scale);
    return { group, muzzle, length: layout.muzzleZ * scale, parts };
  }
}
